import { CLIENT_TYPE_WEB, CLIENT_TYPE_WECHAT, clientType } from '@/apis/api'
import { h, ref } from 'vue'
import { computed } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { currentUser, currentUserHandle, DEMO_ID } from '@/composables/user/users'
import { useWindowSize } from '@vueuse/core'
import { isChromeBrowser, isNotMobileBrowser, inMiniprograme } from '@/utils/device'
import { isNotEmpty, isEmpty } from '@/utils/validate'

// Explicit Re-exports to avoid phantom export errors and maintain backward compatibility
// export { isNotEmpty, isEmpty, stringToBoolean, parseBooleanQueryParam } from '@/utils/validate'
// export { renderMessage, scrollToTop, openBlankLink, simulateClick, findAndScroll, addTemporaryBorder, addTemporaryBackground } from '@/utils/render'
export * from './validate'
export * from './device'
export * from './render'

export const hasPhone = computed(() => {
  return isNotEmpty(currentUser.value?.user?.phone?.phoneNumber || null)
})

const _auditMode = useLocalStorage<string>('auditMode', 'false')
export const auditMode = _auditMode
export const auditModeShow = computed(() => {
  return auditMode.value === 'true' && inMiniprograme.value
})
// export const auditModeShow = ref(true)

const _miniProgramChanged = useLocalStorage<number>('miniProgramChanged', 0)
export const miniProgramChanged = _miniProgramChanged
const _msgId = useLocalStorage<string>('msgId', '0')
export const msgId = _msgId

// 设置localStorage
export const setStorage = function (key: string, obj: any) {
  const json = JSON.stringify(obj)
  window.localStorage.setItem(key, json)
}
// 获取localStorage
export const getStorage = function (key: string) {
  const str = window.localStorage.getItem(key)
  if (str === null || str === undefined || str === '' || str === 'undefined') {
    return null
  }
  return JSON.parse(str)
}

// 移除localStorage
export const removeStorage = function (key: string) {
  window.localStorage.removeItem(key)
}

// 获取浏览器默认语言
export const getBrowserLang = function () {
  if (import.meta.server) return 'zh_CN'
  const browserLang: string = navigator.language || navigator.language
  let defaultBrowserLang = ''
  if (
    browserLang.toLowerCase() === 'cn' ||
    browserLang.toLowerCase() === 'zh' ||
    browserLang.toLowerCase() === 'zh-cn'
  ) {
    defaultBrowserLang = 'zh_CN'
  } else {
    defaultBrowserLang = 'en_US'
  }
  return defaultBrowserLang
}

export function convertRemToPixels(rem: number | string): number {
  if (typeof window === 'undefined') return 0
  if (typeof rem === 'string' && rem.indexOf('rem') !== -1) {
    rem = parseFloat(rem.replace('rem', '').replace(' ', ''))
  }
  return (rem as number) * parseFloat(getComputedStyle(document.documentElement).fontSize)
}

export function getHostWithProtocolFromUrl(url: string) {
  const parsedUrl = new URL(url)
  return `${parsedUrl.protocol}//${parsedUrl.host}`
}

export function rem2Px(rem: number): number {
  if (typeof window === 'undefined') return 0
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
  return rem * rootFontSize
}

export const rem2px = (rem: number | string): number => {
  if (typeof window === 'undefined') return 0
  return (rem as number) * parseFloat(getComputedStyle(document.documentElement).fontSize)
}

export const px2rem = (px: number | string): number => {
  if (typeof window === 'undefined') return 0
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
  return (px as number) / rootFontSize
}

export const px2remStr = (px: number | string): string => {
  if (typeof window === 'undefined') return '0rem'
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
  return (px as number) / rootFontSize + 'rem'
}

export function roundToOneDecimal(num: number) {
  return Math.round(num * 10) / 10
}

export function replaceAll(str: string, search: string, replacement: string): string {
  const searchEscaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(searchEscaped, 'g')
  return str.replace(regex, replacement)
}

export function replacePlaceHolder(source: string, placeholder: string, target: string) {
  return replaceAll(source, placeholder, target)
}

export function replaceStrong(source: string) {
  return replacePlaceHolder(replacePlaceHolder(source, '??', '<strong>'), '**', '</strong>')
}

export const obj = {}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const value = bytes / Math.pow(k, i)
  let formattedValue: string
  if (value % 1 === 0) {
    formattedValue = value.toString()
  } else {
    formattedValue = value.toFixed(1)
    if (formattedValue.endsWith('.0')) {
      formattedValue = parseInt(formattedValue).toString()
    }
  }
  return `${formattedValue}${sizes[i]}`
}

export const WAIT_FOR_LOADING_TIME = 350

export function removeHTMLTags(htmlString: string) {
  return htmlString.replace(/<[^>]*>/g, '')
}

export function containsString(source: string, pattern: string) {
  if (isEmpty(source) || isEmpty(pattern)) {
    return false
  }
  return source.indexOf(pattern) !== -1
}

export const MAX_NAME_CHARS = 10

export function minifyString(source: string, maxLength: number) {
  if (isEmpty(source)) {
    return ''
  } else if (source.length <= maxLength) {
    return source
  } else {
    return source.substring(0, maxLength) + '…'
  }
}

export function getFileExtension(filename: string) {
  const parts = filename.split('.')
  return parts.length > 1 ? parts.pop() : ''
}

export function getFileName(folder: string, suffix: number, originalName: string) {
  let ext = getFileExtension(originalName)
  return (isEmpty(folder) ? 'img/' : folder + '/') + new Date().getTime() + '_' + suffix + '.' + ext
}

export function base64ToFile(base64Data: string | undefined, fileName: string, fileType: string = 'image/jpeg') {
  if (!base64Data) {
    return null
  }
  const sliceIndex = base64Data.indexOf(';base64,') + ';base64,'.length
  const base64Content = base64Data.slice(sliceIndex)
  const byteCharacters = atob(base64Content)
  const byteArrays = []
  for (let chunkOffset = 0; chunkOffset < byteCharacters.length; chunkOffset += 1024) {
    const slice = byteCharacters.slice(chunkOffset, chunkOffset + 1024)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }
  const blob = new Blob(byteArrays, { type: fileType })
  return new File([blob], fileName, { type: fileType })
}

export function cutTextAndEscape(text: string | null | undefined, maxLength: number): string {
  if (!text) return ''
  const escapeCharRegex = /&(?:[a-z]+|#x[a-f0-9]+|#\d+);/gi
  let result = ''
  let index = 0
  let match
  while (index < maxLength && (match = escapeCharRegex.exec(text)) !== null) {
    const escapeStartIndex = match.index
    const escapeEndIndex = escapeCharRegex.lastIndex
    if (escapeStartIndex < maxLength) {
      result += text.slice(index, escapeStartIndex)
      result += match[0]
      index = escapeEndIndex
    } else {
      break
    }
  }
  if (index < maxLength) {
    result += text.slice(index, maxLength)
  }
  if (text.length > maxLength) {
    result += '...'
  }
  return result
}

export const isVideoPlayable = computed(() => {
  if (import.meta.server) return false
  return isChromeBrowser() && isNotMobileBrowser()
})

export const truncateText = (text: string, maxLength: number = 15) => {
  if (text && text.length > maxLength) {
    return text.substring(0, maxLength) + '...'
  }
  return text
}

export function cutText(text: string | null | undefined, maxLength: number) {
  if (text) {
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
  } else {
    return ''
  }
}

export function getPhotoSize(label: string | null | undefined): string {
  if (!label) return ''
  const sizeMap: Record<string, string> = {
    '1寸': '1',
    '2寸': '2',
    '4寸': '4',
    '5寸': '5',
    '6寸': '6',
    '7寸': '7',
    '8寸': '8',
    '10寸': '10',
    '12寸': '12',
    '16寸': '16',
    '18寸': '18',
    '20寸': '20',
    '24寸': '24',
    大1寸: '1x'
  }
  return sizeMap[label] || (label.endsWith('寸') ? label.replace('寸', '') : label)
}

export const isMember = computed(() => {
  return currentUser.value?.member !== null && currentUser.value?.member.state == 1
})
